import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faWindowMinimize } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogTitle } from '@headlessui/react';
import { type ReactNode, useEffect } from 'react';

import { ALERT_DIALOG_Z_CLASS } from '@/constants/layout';

import { Button } from '../Button/Button';
import { cn } from '../lib/utils';
import { Modal, type ModalSize } from '../Modal/Modal';
import { Typography } from '../Typography/Typography';

// Kept identical to Alert's `info` variant, the pairing counselors already
// associate with a member arriving. Shared with AlertDialogPill so a minimized
// alert looks like the same object.
const ALERT_INFO_TEXT = 'text-blue-800 dark:text-blue-200';
const ALERT_INFO_SURFACE = `bg-blue-50 dark:bg-blue-950/30 ${ALERT_INFO_TEXT}`;

interface AlertDialogProps {
  /** Rendered under the description: actions, badges, anything the alert needs. */
  children?: ReactNode;
  className?: string;
  dataTestId?: string;
  description?: ReactNode;
  icon?: IconDefinition;
  /**
   * An alert dialog cannot be dismissed. Supplying this gives it the one way
   * out: a control in the top right, and Escape, that collapse the dialog while
   * leaving whatever raised it still outstanding.
   */
  onMinimize?: () => void;
  open: boolean;
  /** Half the viewport by default: enough to interrupt, not enough to trap. */
  size?: ModalSize;
  title: ReactNode;
  zIndexClassName?: string;
}

/**
 * A dialog that interrupts rather than waiting to be noticed: it announces itself
 * as an alertdialog, paints above the app's fixed overlays, and cannot be closed
 * by the backdrop, Escape or a close button.
 *
 * It borrows the `info` colours from `Alert`, which is what counselors already
 * read as "the member is here". Those are stock Tailwind families, which
 * `ui/style/dark-mode.css` self-maps rather than inverts, so they bring their own
 * dark counterparts; the text colour is restated on the title and description
 * because `Typography` sets a near-black of its own.
 */
function AlertDialog({
  children,
  className,
  dataTestId,
  description,
  icon,
  onMinimize,
  open,
  size = 'lg',
  title,
  zIndexClassName = ALERT_DIALOG_Z_CLASS
}: AlertDialogProps) {
  useEffect(() => {
    if (!open || !onMinimize) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onMinimize();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onMinimize, open]);

  return (
    <Modal
      open={open}
      restrictCloseToButton
      role="alertdialog"
      zIndexClassName={zIndexClassName}
      onClose={() => {}}
      dataTestId={dataTestId}
      size={size}
      className={cn(
        'flex flex-col items-center justify-center text-center',
        ALERT_INFO_SURFACE,
        className
      )}>
      {onMinimize && (
        <Button
          variant="outline"
          color="neutral"
          size="sm"
          leftIcon={faWindowMinimize}
          onClick={onMinimize}
          className="absolute top-4 right-4">
          Minimize
        </Button>
      )}

      <div aria-live="assertive" className="flex flex-col items-center gap-6 px-6 py-10">
        {icon && <FontAwesomeIcon icon={icon} className="h-16 w-16" aria-hidden />}

        {/* Rendering through DialogTitle is what names the dialog for assistive tech. */}
        <Typography as={DialogTitle} variant="display" className={ALERT_INFO_TEXT}>
          {title}
        </Typography>

        {description && (
          <Typography variant="heading" className={ALERT_INFO_TEXT}>
            {description}
          </Typography>
        )}

        {children}
      </div>
    </Modal>
  );
}

export { ALERT_INFO_SURFACE, ALERT_INFO_TEXT, AlertDialog };
export type { AlertDialogProps };
